import { Flex, Menu, MenuProps } from "antd";
import clsx from "clsx";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";

import { Logo } from "@/components/Logo";

// import BarChartIcon from "@/assets/bar-chart.svg?react";
import BookIcon from "@/assets/book.svg?react";
import ProgramsIcon from "@/assets/programs.svg?react";
import NewsIcon from "@/assets/news.svg?react";
import GroupIcon from "@/assets/group.svg?react";
import HourGlassIcon from "@/assets/hourglass.svg?react";
import Reports from "@/assets/reports.svg?react";
import { useMeQuery } from "@/graphql";
import { useTranslation } from "react-i18next";

export const Sider: FC = () => {
  const skipMeQuery = localStorage.getItem("accessToken") === null || localStorage.getItem("refreshToken") === null;
  const { data: meData } = useMeQuery({ skip: skipMeQuery });

  const { t } = useTranslation();

  const { pathname } = useLocation();

  const currLocation = pathname.slice(1).split("/").join("-");

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  const navigationItems: MenuProps["items"] = useMemo(
    () => [
      // {
      //   key: "admin-dashboard",
      //   icon: <BarChartIcon />,
      //   label: <Link to={"dashboard"}>Dashboard</Link>,
      // },
      {
        key: "admin-programs",
        icon: <ProgramsIcon />,
        label: <Link to={"programs"}>{t("sidebar.programs")}</Link>,
      },
      {
        key: "admin-courses",
        icon: <BookIcon />,
        label: <Link to={"courses"}>{t("sidebar.courses")}</Link>,
      },

      {
        key: "admin-users",
        icon: <GroupIcon />,
        label: <Link to={"users"}>{t("sidebar.users")}</Link>,
      },
      {
        key: "content",
        icon: <NewsIcon />,
        label: t("sidebar.content.label"),
        children: [
          { key: "admin-posts", label: <Link to={"posts"}>{t("sidebar.content.posts")}</Link> },
          { key: "admin-notifications", label: <Link to={"notifications"}>{t("sidebar.content.notifications")}</Link> },
          { key: "admin-protected-terms", label: <Link to={"protected-terms"}>{t("sidebar.content.protected-terms")}</Link> },
        ],
      },
      {
        key: "reports",
        icon: <Reports />,
        label: t("sidebar.reports"),
        children: [
          { key: "admin-reports-programs", label: <Link to={"reports/programs"}>{t("sidebar.programs")}</Link> },
          { key: "admin-reports-courses", label: <Link to={"reports/courses"}>{t("sidebar.courses")}</Link> },
          { key: "admin-reports-users", label: <Link to={"reports/users"}>{t("sidebar.users")}</Link> },
        ],
      },
      {
        key: "admin-activity-log",
        icon: <HourGlassIcon />,
        label: <Link to={"activity-log"}>{t("sidebar.logs")}</Link>,
      },
    ],
    [t]
  );

  const submenuOf = useMemo(
    () =>
      new Map(
        (navigationItems ?? []).flatMap((item) =>
          item && "children" in item && item.children ? item.children.map((child) => [String(child?.key), String(item.key)] as const) : []
        )
      ),
    [navigationItems]
  );

  const routeSubmenu = useMemo(
    () => [...submenuOf.entries()].find(([key]) => currLocation === key || currLocation.startsWith(`${key}-`))?.[1] ?? null,
    [currLocation, submenuOf]
  );

  useEffect(() => {
    setActiveSubmenu(routeSubmenu);

    if (routeSubmenu) setOpenKeys((prev) => (prev.includes(routeSubmenu) ? prev : [...prev, routeSubmenu]));
  }, [routeSubmenu]);

  const menuItems = useMemo(
    () =>
      (navigationItems ?? [])
        .filter(
          (item) =>
            !(item?.key && ["admin-users", "content", "admin-activity-log"].includes(item.key as string) && meData?.me?.role?.type?.includes("creator"))
        )
        .map((item) =>
          item && "children" in item ? { ...item, className: clsx("custom-submenu-title", { "custom-submenu-title-active": item.key === activeSubmenu }) } : item
        ),
    [activeSubmenu, meData?.me?.role?.type, navigationItems]
  );

  return (
    <Flex rootClassName="sider-container" vertical align="center" gap={"120px"}>
      <Logo type="admin" className="sider-logo" />
      <Flex rootClassName="navigation-menu-container" vertical>
        <Menu
          rootClassName="navigation-menu"
          mode="inline"
          items={menuItems}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onClick={({ key }) => setActiveSubmenu(submenuOf.get(key) ?? null)}
          defaultSelectedKeys={currLocation ? [currLocation] : undefined}
        />
      </Flex>
    </Flex>
  );
};
